import type { BgId, Hex, Widget } from '../contract.js';
import { copy } from '../copy.js';
import { FROST_SWATCH, bgCss, bgId } from '../grid/background.js';
import { findIt, span, zRange } from '../grid/engine.js';
import { FS_LADDER, textFsOf } from '../grid/text.js';
import type { AppState } from '../state/types.js';
import { color, colourName, icon, palette } from '../tokens.js';

/** The toolbar of the selected widget and its swatch panel, ported from `toolbarVals` (02 §3.27–3.28; 04 §10). */

export type ToolAction =
  | { readonly kind: 'textSize'; readonly dir: -1 | 1 }
  | { readonly kind: 'zoom'; readonly factor: number }
  | { readonly kind: 'swatches' }
  | { readonly kind: 'changeSong' }
  | { readonly kind: 'remove' };

export interface ToolButton {
  readonly aria: string;
  readonly label: string;
  /** Icon path, if any. */
  readonly d: string;
  /** Colour dot (the Background button). */
  readonly dot: string | null;
  readonly off: boolean;
  readonly op: number;
  readonly col: string;
  readonly pad: string;
  readonly pressed: boolean;
  readonly bg: string;
  readonly action: ToolAction;
}
export interface Swatch { readonly v: BgId; readonly aria: string; readonly pressed: boolean; readonly bg: string; readonly isNone: boolean; readonly ringW: string }
export interface ToolbarVals {
  readonly id: string;
  readonly x: number; readonly y: number;
  readonly aria: string;
  readonly items: readonly ToolButton[];
  readonly swatches: { readonly x: number; readonly y: number; readonly rows: readonly { readonly label: string; readonly items: readonly Swatch[] }[] } | null;
}

export function toolbarVals(st: AppState, items: readonly Widget[], owner: Hex): ToolbarVals | null {
  const it = st.sel ? findIt(items, st.sel) : null;
  // A voice item is never selected (G12); the guard keeps the toolbar from ever describing one.
  if (!it || it.t === 'voice' || st.pdrag) return null;
  const W = span(it.w), x = it.c * 82 + W / 2, above = it.r > 0, y = above ? it.r * 82 - 54 : (it.r + it.h) * 82 - 14 + 10;
  const hasBg = it.t === 'text', cur = hasBg ? bgId(it) : null;
  type Draft = { aria: string; label?: string; d?: string; dot?: string; off?: boolean; danger?: boolean; pressed?: boolean; action: ToolAction };
  const B: Draft[] = [];
  if (it.t === 'text') {
    const fs = textFsOf(it);
    B.push({ aria: copy.edit.smaller, d: icon.minus, off: fs <= (FS_LADDER[0] ?? 12), action: { kind: 'textSize', dir: -1 } });
    B.push({ aria: copy.edit.larger, d: icon.plusSmall, off: fs >= (FS_LADDER[FS_LADDER.length - 1] ?? 44), action: { kind: 'textSize', dir: 1 } });
  }
  if (it.t === 'photo' || it.t === 'video') {
    const [min, max] = zRange(), z = it.z || 1;
    B.push({ aria: copy.edit.zoomOut, d: icon.minus, off: z <= min + 0.001, action: { kind: 'zoom', factor: 1 / 1.2 } });
    B.push({ aria: copy.edit.zoomIn, d: icon.plusSmall, off: z >= max - 0.001, action: { kind: 'zoom', factor: 1.2 } });
  }
  if (hasBg) B.push({ label: copy.edit.background, aria: copy.edit.background, dot: bgCss(cur, owner), pressed: st.swOpen, action: { kind: 'swatches' } });
  if (it.t === 'song') B.push({ label: copy.edit.changeSong, aria: copy.edit.changeSong, d: icon.song, action: { kind: 'changeSong' } });
  B.push({ label: copy.edit.remove, aria: copy.edit.removeThis(it.t), d: icon.remove, danger: true, action: { kind: 'remove' } });

  let swatches: ToolbarVals['swatches'] = null;
  if (hasBg && st.swOpen) {
    const solids: { v: BgId; aria: string; none?: boolean; frost?: boolean }[] = [
      { v: 'none', aria: copy.edit.swatch.none, none: true }, { v: 'tile', aria: copy.edit.swatch.tile }, { v: 'light', aria: copy.edit.swatch.light },
      { v: 'frost', aria: copy.edit.swatch.frost, frost: true }, { v: 'own', aria: copy.edit.swatch.own },
    ];
    for (const c of palette) if (c !== owner) solids.push({ v: c as BgId, aria: copy.edit.swatch.colour(colourName(c)) });
    const gradients: { v: BgId; aria: string; none?: boolean; frost?: boolean }[] = [
      { v: 'g:own', aria: copy.edit.swatch.gOwn }, { v: 'g:dusk', aria: copy.edit.swatch.gDusk }, { v: 'g:ember', aria: copy.edit.swatch.gEmber },
      { v: 'g:tide', aria: copy.edit.swatch.gTide }, { v: 'g:bloom', aria: copy.edit.swatch.gBloom }, { v: 'g:meadow', aria: copy.edit.swatch.gMeadow },
    ];
    const swatch = (s: { v: BgId; aria: string; none?: boolean; frost?: boolean }): Swatch => {
      const on = cur === s.v || (s.v === 'own' && cur === owner);
      return { v: s.v, aria: on ? copy.edit.swatch.selected(s.aria) : s.aria, pressed: on, bg: s.frost ? FROST_SWATCH : bgCss(s.v, owner), isNone: !!s.none, ringW: on ? '2px' : '0px' };
    };
    swatches = { x: Math.round(x), y: above ? y - 106 : y + 54, rows: [{ label: copy.edit.solid, items: solids.map(swatch) }, { label: copy.edit.gradient, items: gradients.map(swatch) }] };
  }

  return {
    id: it.id, x: Math.round(x), y, aria: copy.edit.toolbar,
    items: B.map((b): ToolButton => ({
      aria: b.aria, label: b.label ?? '', d: b.d ?? '', dot: b.dot ?? null, off: !!b.off, op: b.off ? 0.35 : 1,
      col: b.danger ? color.danger : color.textStrong, pad: b.label ? '0 14px 0 10px' : '0', pressed: !!b.pressed, bg: b.pressed ? 'rgba(255, 255, 255, 0.1)' : 'transparent', action: b.action,
    })),
    swatches,
  };
}
